/**
 * The enum values here are what will end up in our observability systems.
 *
 * In SignalFX you can find the following metrics using the following examples:
 *
 * When used in page load:
 * `A = data('experience.ufo.count', filter=filter('experience_key', 'canvas.fe.load.{{MetricName}}'), rollup='sum', extrapolation='zero').sum(by=['experience_status']).publish(label='A')`
 *
 * When used in ReliabilityMetrics:
 * `A = data('experience.ufo.count', filter=filter('experience_key', 'canvas.fe.operation.{{MetricName}}'), rollup='sum', extrapolation='zero').sum(by=['experience_status']).publish(label='A')`
 *
 * You can use this splunk dashboard to investigate errors https://splunk.paas-inf.net/en-US/app/search/whiteboards_slislo
 */

enum Board {
  BOARD_LOAD = 'board-page',
  BOARD_LOAD_ONLINE = 'board-page-online',
  BOARD_LOAD_ANONYMOUS = 'board-page-anonymous',
  BOARD_LOAD_EMBEDDED = 'board-page-embedded',
  BOARD_LOAD_MOBILE = 'board-page-mobile',
  BOARD_LOAD_HISTORICAL = 'board-page-historical-version',
  BOARD_LOAD_HISTORICAL_LATEST = 'board-page-latest-historical-version',
  BOARD_LOAD_PREVIEW = 'board-page-preview',
  BOARD_RENDERED = 'board-rendered',
  COLLAB_CONNECT = 'collab-connect',
  COLLAB_CONNECT_NCS = 'collab-connect-ncs',
  WEBSOCKET_CONNECT = 'websocket-connect',
  WEBSOCKET_CONNECT_NCS = 'websocket-connect-ncs',
  MEDIA_UPLOAD = 'media-upload',
  MEDIA_DOWNLOAD = 'media-download',
  FILE_IMPORT = 'file-import',
  PDF_WHITEBOARD_IMPORT_MIRO = 'pdf-whiteboard-import-miro',
  PDF_WHITEBOARD_IMPORT_MURAL = 'pdf-whiteboard-import-mural',
  RENDERED_FILE_EXPORT = 'rendered-file-export',
  GL_CONTEXT_CREATED = 'gl-context-created',
  ACCELERATED_HARDWARE = 'accelerated-hardware',
  DISPLAY_SMART_LINK = 'display-smart-link',
  DISPLAY_AUTHOR_LABELS = 'display-author-labels',
  AI_GENERATE_STICKIES_REQUEST = 'ai-generate-stickies-request',
  AI_CLUSTERING_API_REQUEST = 'ai-clustering-api-request',
  TRANSFORM_DOCUMENT_W_TO_Y_INTERACTION = 'transform-document-w-to-y-interaction',
  TRANSFORM_DOCUMENT_Y_TO_W_INTERACTION = 'transform-document-y-to-w-interaction',
  SPA_TRANSITION = 'spa-transition',
  GRAPH_ENGINE_LAYOUT_OPERATION = 'graph-engine-layout-operation',
  SEND_MENTION_NOTIFICATION = 'send-mention-notification',
  CHECK_MENTION_PERMISSIONS = 'check-mention-permissions',
  CREATE_DATABASE_FROM_MORE_MENU = 'create-database-from-more-menu',
  DATABASE_BRIDGE_HANDSHAKE = 'database-bridge-handshake',
}

enum Slides {
  // Whole presenter session — start on enter, end on exit. Captures crash/abort if user leaves.
  SLIDES_PRESENTER_SESSION = 'slides-presenter-session',
  // Reliability of the entry transition (e.g. fullscreen acquire, abilities applied).
  SLIDES_PRESENTER_ENTER = 'slides-presenter-enter',
  // Reliability of the exit cleanup.
  SLIDES_PRESENTER_EXIT = 'slides-presenter-exit',

  // Measures whether a user can move between slides via guided navigation
  // (keyboard arrows, mouse wheel, filmstrip click, etc.). The experience
  // is started for every navigation request, succeeds once focus is
  // established on a target slide, and fails on an unexpected error during
  // focus. No-op outcomes (no navigable entities, boundary hit) are marked
  // ABORTED so they don't skew the reliability SLI.
  SLIDES_NAVIGATE = 'slides-navigate',

  // Reliability of the slides export operation. Starts when a
  // supported export is requested and ends after the export file is created or
  // the export pipeline throws.
  SLIDES_EXPORT = 'slides-export',

  // ---------------------------------------------------------------------------
  // AI generation funnel for the Slides LLM streaming pipeline.
  // See modules/slides/module/src/behaviour/ametrics for the
  // generic AiGenerationFunnel that emits these. The funnel covers the path
  // from a Rovo command being received → first chunk → first parsed partial →
  // first board write → completion / abort / failure.
  // ---------------------------------------------------------------------------

  // Top-level SLI: one good/bad signal per AI deck generation (create or update).
  // Carries `errorCategory` and `lastReachedStage` on terminal transitions so
  // dashboards can cross-tab failures by where they died.
  SLIDES_AI_GENERATION = 'slides-ai-generation',
  // Counter emitted at every funnel stage transition. Faceted by `stage` so
  // SignalFx can render the drop-off chart.
  SLIDES_AI_GENERATION_STAGE = 'slides-ai-generation-stage',
  // Counter emitted whenever a (terminal or non-terminal) error is classified
  // during AI generation. Faceted by `category` (network | parser | schema |
  // repair-exhausted | timeout | unknown).
  SLIDES_AI_GENERATION_ERROR = 'slides-ai-generation-error',
  // Histogram: time from `command_received` to the first stream chunk.
  SLIDES_AI_GENERATION_TIME_TO_FIRST_CHUNK = 'slides-ai-generation-time-to-first-chunk',
  // Histogram: end-to-end duration from `command_received` to terminal state.
  SLIDES_AI_GENERATION_DURATION = 'slides-ai-generation-duration',
  // Histogram: number of times the JSON repair path was invoked for one deck
  // generation. A non-zero value indicates the streaming JSON parser produced
  // unusable output and we fell back to repair-slide-json.
  SLIDES_AI_GENERATION_JSON_REPAIR_INVOCATIONS = 'slides-ai-generation-json-repair-invocations',
}

enum Maui {
  MAUI_APP_LOAD = 'maui-app-load',
  // Reliability of committing an inline text edit in an embedded MAUI app.
  // Instrumented as a UFO operation experience by the maui-service outer-iframe
  // edit bridge; publishes as `canvas.fe.operation.maui-app-edit`.
  MAUI_APP_EDIT = 'maui-app-edit',
}

enum Database {
  DATABASE_PANEL_VIEW_SETTINGS_OPEN = 'panel-view-settings-open',
  DATABASE_TOOLBAR_TOOLBAR_RENDER_VIEWS = 'toolbar-render-view-tabs',
  DATABASE_TOOLBAR_VIEWS_CHANGE_VIEW = 'toolbar-views-change-view',
}

enum DatabasePerformance {
  DATABASE_ADD_ENTRY_TIME = 'add-entry-time',
  DATABASE_VIRTUALIZED_ROWS_RENDER_TIME = 'virtualized-rows-render-time',
  DATABASE_ENTRY_MUTATION_TIME = 'entry-mutation-time',
  DATABASE_FIELD_MUTATION_TIME = 'field-mutation-time',
  DATABASE_VALUE_MUTATION_TIME = 'value-mutation-time',
  DATABASE_HEADER_CHECKBOX_INTERACTION_TIME = 'header-checkbox-interaction-time',
  DATABASE_ROW_CHECKBOX_INTERACTION_TIME = 'row-checkbox-interaction-time',
  DATABASE_CELL_SELECTION_INTERACTION_TIME = 'cell-selection-interaction-time',
  DATABASE_CELL_INLINE_EDIT_OPEN_INTERACTION_TIME = 'cell-inline-edit-open-interaction-time',
  DATABASE_CELL_CONTEXT_MENU_OPEN_INTERACTION_TIME = 'cell-context-menu-open-interaction-time',
}

enum DatabaseReliability {
  DATABASE_SELECTED = 'database-selected',
  DATABASE_ADD_ENTRY = 'add-entry',
  DATABASE_DELETE_ENTRY = 'delete-entry',
  DATABASE_ADD_FIELD = 'add-field',
  DATABASE_DELETE_FIELD = 'delete-field',
  DATABASE_RENAME_FIELD = 'rename-field',
  DATABASE_UPDATE_FROZEN_COLUMNS = 'update-frozen-columns',
  DATABASE_SET_VALUE = 'set-value', // we might need more granular metrics for this, if we see a lot of failures here
  DATABASE_SAVE_VIEW = 'save-view',
}

export enum ECSHealth {
  ECS_LOOP_PERFORMANCE = 'ecs-loop-performance',
}

enum AppStartUp {
  // Starts when app first starts, ends when board-page has mounted.
  APP_MOUNTED = 'app-mounted',
  // Starts when app first starts, ends when we have received an authorization
  // token from the parent product (e.g., Confluence).
  APP_AUTHORIZED = 'app-authorized',
  // Starts when app first starts, ends when we have the board contents which
  // the renderer can use to begin rendering.
  APP_SESSION_INIT = 'app-session-init',
  // Starts when the world is booted, ends once the world has rendered board contents.
  APP_RENDERER_INIT = 'app-renderer-init',
}

enum AppCollab {
  // Starts when the app first starts, ends once IndexedDB has returned data.
  APP_CONNECTED_TO_LOCAL = 'app-connected-to-local-document',
  // Starts when the app first starts, ends once a remote connection has been
  // established, and all synchronisation with remote is complete.
  APP_CONNECTED_TO_REMOTE = 'app-connected-to-remote-document',
  // Measures the roundtrip time for a step from client send to server echo receipt.
  // This represents the client-perceived latency for collaborative changes.
  STEP_ROUNDTRIP_NCS = 'step-roundtrip-ncs',
  STEP_ROUNDTRIP_CF = 'step-roundtrip-cf',
  // Counts received updates that leave the local Yjs doc with pending data.
  LAZY_SYNC_UNAPPLIED_UPDATE = 'lazy-sync-unapplied-update',
  // Counts peer-to-peer lazy sync requests sent to recover pending data.
  LAZY_SYNC_REQUEST_SENT = 'lazy-sync-request-sent',
  // Counts lazy sync requests that did not complete before the timeout.
  LAZY_SYNC_TIMEOUT = 'lazy-sync-timeout',
  // Histogram of request-to-catch-up latency for lazy sync.
  LAZY_SYNC_LATENCY = 'lazy-sync-latency',
  // Measures NCS connect-time catch-up flow via document status and gap-closing steps.
  CLOSE_CONNECT_TIME_GAP_NCS = 'close-connect-time-gap-ncs',
  // Measures latency/reliability of the NCS document status endpoint.
  GET_DOCUMENT_STATUS_NCS = 'get-document-status-ncs',
  // Counts C3 transport error events surfaced to the whiteboard connection.
  COLLAB_CONNECTION_TRANSPORT_ERROR = 'collab-connection-transport-error',
  // Counts C3 domainError events surfaced to the whiteboard connection.
  COLLAB_CONNECTION_TRANSPORT_DOMAIN_ERROR = 'collab-connection-transport-domain-error',
  // Counts NCS socket restarts triggered by dropped updates.
  SOCKET_RESTART_NCS_DROPPED_UPDATE = 'socket-restart-ncs-dropped-update',
  // Counts NCS socket restarts triggered by confirm mismatches.
  SOCKET_RESTART_NCS_CONFIRM_MISMATCH = 'socket-restart-ncs-confirm-mismatch',
  // Counts NCS socket restarts triggered by ack timeouts.
  SOCKET_RESTART_NCS_ACK_TIMEOUT = 'socket-restart-ncs-ack-timeout',
  // Counts NCS socket restarts triggered by lazy sync timeouts.
  SOCKET_RESTART_NCS_LAZY_SYNC_TIMEOUT = 'socket-restart-ncs-lazy-sync-timeout',
  // Counts NCS socket restarts triggered by incomplete backend agent operations.
  SOCKET_RESTART_NCS_BACKEND_AGENT_OPERATION_TIMEOUT = 'socket-restart-ncs-backend-agent-operation-timeout',
  // Counts CF socket restarts triggered by dropped updates.
  SOCKET_RESTART_CF_DROPPED_UPDATE = 'socket-restart-cf-dropped-update',
  // Counts CF socket restarts triggered by incomplete backend agent operations.
  SOCKET_RESTART_CF_BACKEND_AGENT_OPERATION_TIMEOUT = 'socket-restart-cf-backend-agent-operation-timeout',
}

enum AppResource {
  // Starts when a spritesheet begins loading, ends once it succeeds or fails loading.
  SPRITESHEET_LOADED = 'spritesheet-loaded',
  // Starts when a spritesheet begins loading, ends once it succeeds or fails to
  // load from the cache.
  SPRITESHEET_LOADED_CACHED = 'spritesheet-loaded-cached',
  // Starts when the app first starts loading, ends once all initial text
  // elements have completed rendering - tracks spritesheets used.
  SPRITESHEET_LOADED_INITIAL = 'spritesheet-loaded-initial',
  // Starts when the app first starts loading, ends once all initial text
  // elements have completed rendering.
  INITIAL_TEXT_RENDERED = 'initial-text-rendered',
  // Starts when a font family begins loading, ends once it has loaded.
  FONT_FAMILY_LOADED = 'font-family-loaded',
  // Starts when entitlement response is loaded from token and validated against
  // the paywall feature schema
  PAYWALL_FEATURE_SCHEMA_VALIDATION = 'paywall-feature-schema-validation',
  // Starts when policies response is loaded from token and validated against the
  // policies schema
  POLICIES_SCHEMA_VALIDATION = 'policies-schema-validation',
  // Starts when starting to fetch the locale messages and creating the intl object
  // Ends when the intl object is created and the locale messages are fetched
  INTL_MESSAGES_LOADED = 'intl-messages-loaded',
  // Starts when Statsig client starts initalizing
  // Ends when Statsig client finishes initializing
  STATSIG_CLIENT_INITIALIZED = 'statsig-client-initialized',
}

// Metrics for the web workers that Canvas depends upon
enum AppWorker {
  // The text layout worker
  LAYOUT_WORKER = 'layout-worker',

  // The text search worker, used for searching text in the board
  TEXT_SEARCH_WORKER = 'text-search-worker',

  // Eventually: the AI worker
  AI_TEAMMATE_WORKER = 'ai-teammate-worker',
}

export enum UserInteraction {
  DRAG_INTERACTION = 'drag-interaction',
  RESIZE_INTERACTION = 'resize-interaction',
  RESIZE_PATH_INTERACTION = 'resize-path-interaction',
  ROTATE_INTERACTION = 'rotate-interaction',
  PAN_BOARD_INTERACTION = 'pan-board-interaction',
  CREATE_SMART_CONNECTOR_INTERACTION = 'create-smart-connector-interaction',
  UPDATE_SMART_CONNECTOR_INTERACTION = 'update-smart-connector-interaction',
  UNLINK_SMART_CONNECTOR_INTERACTION = 'unlink-smart-connector-interaction',
  EXPORT_JIRA_ISSUE_INTERACTION = 'export-jira-issue-interaction',
  CREATE_SMART_SECTION_INTERACTION = 'create-smart-section-interaction',
  APPLY_SMART_SECTION_INTERACTION = 'apply-smart-section-interaction',
  INSERT_TEMPLATE_INTERACTION = 'insert-template-interaction',
  JIRA_COMMUNICATION_INTERACTION = 'jira-communication-interaction',
  JIRA_IMPORT_INTERACTION = 'jira-import-interaction',
  JIRA_ISSUE_UNFURL_INTERACTION = 'jira-issue-unfurl-interaction',
  JIRA_SEARCH_INTERACTION = 'jira-search-interaction',
  JIRA_SEARCH_NLP_INTERACTION = 'jira-search-nlp-interaction',
  PAYWALL_CTA_INTERACTION = 'paywall-cta-interaction',
  EXPORT_RENDERED_FILE_INTERACTION = 'export-rendered-file-interaction',
  COMMENT_INTERACTION = 'comment-interaction',
  TEXT_SEARCH_INTERACTION = 'text-search-interaction',
  AI_GENERATE_STICKIES_INTERACTION = 'ai-generate-stickies-interaction',
  AI_CLUSTERING_INTERACTION = 'ai-clustering-interaction',
  VOTING_INTERACTION = 'voting-interaction',
  PASTE_INTERACTION = 'paste-interaction',
  CREATE_DOCUMENT_INTERACTION = 'create-document-interaction',
  CREATE_MINDMAP_INTERACTION = 'create-mindmap-interaction',
  AI_AGENT_INTERACTION_GENERATING_FIRST_TOKEN = 'ai-agent-interaction-generating-first-token',
  AI_AGENT_INTERACTION_GENERATING = 'ai-agent-interaction-generating',
  PEN_DRAWING_INTERACTION = 'pen-drawing-interaction',
  CREATE_TABLE_INTERACTION = 'create-table-interaction',
  TABLE_STRUCTURE_INTERACTION = 'table-structure-interaction',
  RESIZE_TABLE_TRACK_INTERACTION = 'resize-table-track-interaction',
  STYLE_TABLE_CELL_INTERACTION = 'style-table-cell-interaction',
  GROUPING_INTERACTION = 'grouping-interaction',
  AI_FIRST_CREATION_EDIT_INTERACTION = 'ai-first-creation-edit-interaction',
}

export type UserInteractionReliability = `${UserInteraction}.reliability`;
export type UserInteractionPerformance = `${UserInteraction}.performance`;

export enum ErrorMetrics {
  CRASH_FREE_SESSION = 'crash-free-session',
  FATAL_ERROR = 'fatal-error',
  NON_FATAL_ERROR = 'non-fatal-error',
}

export const MetricName = {
  ...AppStartUp,
  ...AppCollab,
  ...AppWorker,
  ...AppResource,
  ...Board,
  ...Slides,
  ...Maui,
  ...Database,
  ...DatabasePerformance,
  ...DatabaseReliability,
  ...ECSHealth,
  ...ErrorMetrics,
  ...UserInteraction,
};

export type MetricNameValue =
  | (typeof MetricName)[keyof typeof MetricName]
  | UserInteractionReliability
  | UserInteractionPerformance;
